import type { MenuModifier, MenuModifierSet, MenuItemDetail } from '@/shared/api/menu'
import type { CartItemModifier } from '@/shared/types/cart'
import { ModifierRow, ModifierLabel, ModifierPrice } from './TicketItemModifier.styled'

interface TicketItemModifierProps {
	modifier: CartItemModifier
	menuItem: MenuItemDetail
	// Base price of the parent cart item in cents — required to compute percent-type modifier prices.
	// When omitted, prices are not shown (e.g. order confirmation views where price is irrelevant).
	itemBasePrice?: number
	locale?: string
	currency?: string
	depth?: number
}

// Recursive search: returns the set and modifier at any depth in the tree.
// Returns null when the ref is not found (data inconsistency guard).
function findModifierAndSet(
	modifierSets: MenuModifierSet[],
	modifierRef: string,
	setRef: string,
): { mod: MenuModifier; set: MenuModifierSet } | null {
	for (const set of modifierSets) {
		if (set.ref === setRef) {
			const mod = set.modifiers.find((m) => m.ref === modifierRef)
			if (mod) return { mod, set }
		}
		for (const modifier of set.modifiers) {
			const deep = findModifierAndSet(modifier.modifierSets, modifierRef, setRef)
			if (deep) return deep
		}
	}
	return null
}

// Renders one modifier line and recurses into any child modifiers.
// Ember equivalent: app/templates/components/order-details/ticket-item/modifier.hbs
export default function TicketItemModifier({
	modifier,
	menuItem,
	itemBasePrice,
	locale = 'en-US',
	currency = 'USD',
	depth = 0,
}: TicketItemModifierProps) {
	const result = findModifierAndSet(
		menuItem.modifierSets,
		modifier.modifierRef,
		modifier.modifierSetRef,
	)

	if (!result) return null

	const { mod } = result

	// Compute unit price in cents — percent modifier uses itemBasePrice as the base.
	// Only show when itemBasePrice is provided and the price is non-zero.
	let priceLabel: string | null = null
	if (itemBasePrice !== undefined) {
		const unitPrice = mod.percent > 0 ? itemBasePrice * mod.percent : mod.price
		if (unitPrice > 0) {
			priceLabel = `(+${new Intl.NumberFormat(locale, { style: 'currency', currency }).format(unitPrice / 100)})`
		}
	}

	return (
		<>
			<ModifierRow depth={depth}>
				<ModifierLabel>
					{depth > 0 ? (
						<>
							{/* Arrow is decorative — the indent conveys nesting visually. */}
							<span aria-hidden="true">↳ </span>
							{mod.name}
						</>
					) : (
						mod.name
					)}
					{priceLabel ? <ModifierPrice>{priceLabel}</ModifierPrice> : null}
				</ModifierLabel>
			</ModifierRow>

			{modifier.modifiers.map((child) => (
				<TicketItemModifier
					key={child.modifierRef}
					modifier={child}
					menuItem={menuItem}
					itemBasePrice={itemBasePrice}
					locale={locale}
					currency={currency}
					depth={depth + 1}
				/>
			))}
		</>
	)
}
